import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { LucideIcon } from 'lucide-react';
import { ChevronDown, ChevronRight, Info, Layers, List, MoreVertical } from 'lucide-react';

import { useCreditMutations } from '../../../hooks/useApi';
import { cn } from '../../../lib/utils';
import type { LedgerEntrySummary } from '../../../services/api';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import {
    adminCreditBadgeClassName,
    canAdjustAdminLedgerRow,
    canCancelPendingCreditReservation,
    canRefundAdminLedgerRow,
    canReassignAdminLedgerRow,
    pickGroupedRowActionEntry,
    formatLedgerUsageAmount,
    formatLinkedArtifactsTypeLabel,
    getLedgerTypeDisplayLabel,
    getLedgerUsageAmount,
    getPoolLabel,
    ledgerPoolBadgeClass,
} from './adminCreditAdapters';
import { CreditCopyButton } from './CreditCopyButton';
import { LedgerEntryDetailsDialog } from './ledgerDialogs';
import {
    applyReservationAwareLedgerStates,
    chronologicallyFirstLedgerEntry,
    compositeLedgerState,
    filterLedgerEntriesExcludingPoolTypes,
    groupLedgerByLinkedOrBatch,
    groupLatestTimestamp,
    ledgerEntrySectionKey,
    ledgerEntriesNewestFirst,
    sortPoolTypesForDisplay,
    type SowLedgerTableViewMode,
} from './sowLedgerTableGrouping';

function groupedLedgerUsageLabel(section: string): 'Reserved' | 'Spent' {
    return section === 'pending' ? 'Reserved' : 'Spent';
}

type SowLedgerTableSectionProps = {
    ledgerEntries: LedgerEntrySummary[];
    poolTypes?: string[];
    onAdjustRow: (entry: LedgerEntrySummary) => void;
    onRefundRow: (entry: LedgerEntrySummary) => void;
    onReassignRow: (entry: LedgerEntrySummary) => void;
};

export function SowLedgerTableSection({
    ledgerEntries,
    poolTypes,
    onAdjustRow,
    onRefundRow,
    onReassignRow,
}: SowLedgerTableSectionProps) {
    const { cancelCreditReservation, commitCreditReservation } = useCreditMutations();
    const [viewMode, setViewMode] = useState<SowLedgerTableViewMode>('grouped');
    const [expandedGroupKeys, setExpandedGroupKeys] = useState<Record<string, boolean>>({});
    const [excludedPoolTypes, setExcludedPoolTypes] = useState<Set<string>>(() => new Set());
    const [cancellingReservationId, setCancellingReservationId] = useState<string | null>(null);
    const [committingReservationId, setCommittingReservationId] = useState<string | null>(null);
    const [detailsEntry, setDetailsEntry] = useState<LedgerEntrySummary | null>(null);
    const tableColSpan = 9;

    const availablePoolTypes = useMemo(() => {
        const types = new Set<string>(poolTypes ?? []);
        for (const entry of ledgerEntries) {
            types.add(entry.poolType);
        }
        return sortPoolTypesForDisplay(types);
    }, [ledgerEntries, poolTypes]);
    const availablePoolTypesKey = availablePoolTypes.join('|');

    useEffect(() => {
        setExcludedPoolTypes(new Set());
    }, [availablePoolTypesKey]);

    useEffect(() => {
        setExpandedGroupKeys({});
    }, [viewMode, excludedPoolTypes]);

    const reservationAwareLedgerEntries = useMemo(
        () => applyReservationAwareLedgerStates(ledgerEntries),
        [ledgerEntries],
    );

    const filteredLedgerEntries = useMemo(
        () =>
            filterLedgerEntriesExcludingPoolTypes(reservationAwareLedgerEntries, excludedPoolTypes),
        [excludedPoolTypes, reservationAwareLedgerEntries],
    );

    const groupedEntries = useMemo(
        () => groupLedgerByLinkedOrBatch(filteredLedgerEntries),
        [filteredLedgerEntries],
    );

    const rawEntries = useMemo(
        () => ledgerEntriesNewestFirst(filteredLedgerEntries),
        [filteredLedgerEntries],
    );

    const togglePoolType = (poolType: string) => {
        setExcludedPoolTypes((current) => {
            const next = new Set(current);
            if (next.has(poolType)) {
                next.delete(poolType);
            } else {
                next.add(poolType);
            }
            return next;
        });
    };

    const toggleGroup = (groupKey: string) => {
        setExpandedGroupKeys((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
    };

    const handleCancelReservation = async (entry: LedgerEntrySummary) => {
        const reservationId = entry.creditReservationId?.trim();
        if (!reservationId) {
            return;
        }
        const confirmed = window.confirm(
            'Cancel this in-progress reservation and release the hold?',
        );
        if (!confirmed) {
            return;
        }
        setCancellingReservationId(reservationId);
        try {
            await cancelCreditReservation.mutateAsync({
                reservationId,
                request: {
                    idempotencyKey: `admin-cancel-${reservationId}-${Date.now()}`,
                },
            });
        } catch (error) {
            console.error(error);
            window.alert(
                error instanceof Error ? error.message : 'Could not cancel in-progress reservation',
            );
        } finally {
            setCancellingReservationId(null);
        }
    };

    const handleCommitReservation = async (entry: LedgerEntrySummary) => {
        const reservationId = entry.creditReservationId?.trim();
        if (!reservationId) {
            return;
        }
        const confirmed = window.confirm(
            'Commit this in-progress reservation and record its usage as done?',
        );
        if (!confirmed) {
            return;
        }
        setCommittingReservationId(reservationId);
        try {
            await commitCreditReservation.mutateAsync({
                reservationId,
                request: {
                    idempotencyKey: `admin-commit-${reservationId}-${Date.now()}`,
                },
            });
        } catch (error) {
            console.error(error);
            window.alert(
                error instanceof Error ? error.message : 'Could not commit in-progress reservation',
            );
        } finally {
            setCommittingReservationId(null);
        }
    };

    const rowActionProps: LedgerRowActionProps = {
        ledgerEntries,
        onAdjustRow,
        onRefundRow,
        onReassignRow,
        onCommitReservation: handleCommitReservation,
        onCancelReservation: handleCancelReservation,
        committingReservationId,
        cancellingReservationId,
    };

    const renderRawRow = (entry: LedgerEntrySummary) => (
        <LedgerEntryRow
            key={entry.id}
            entry={entry}
            onShowDetails={setDetailsEntry}
            rowActionProps={rowActionProps}
        />
    );

    const renderGroup = ({
        groupKey,
        entries,
    }: {
        groupKey: string;
        entries: LedgerEntrySummary[];
    }) => {
        if (entries.length === 1) {
            return renderRawRow(entries[0]!);
        }

        const expanded = !!expandedGroupKeys[groupKey];
        const oldest = chronologicallyFirstLedgerEntry(entries);
        const compositeDescription = oldest?.description?.trim() ? oldest.description : '—';
        const poolTypesInGroup = [...new Set(entries.map((e) => e.poolType))];
        const latestTs = groupLatestTimestamp(entries);
        const groupAmountText = formatLedgerUsageAmount(
            entries.reduce((total, item) => total + getLedgerUsageAmount(item), 0),
        );
        const artifactSample = entries.find(
            (item) => item.linkedArtifactType?.trim() && item.linkedArtifactId?.trim(),
        );
        const representativeEntry = pickGroupedRowActionEntry(entries);
        const groupedUsageLabel = groupedLedgerUsageLabel(compositeLedgerState(entries));

        return (
            <Fragment key={groupKey}>
                <TableRow className="bg-neutral-50/90 hover:bg-neutral-50">
                    <TableCell className="w-10 !pr-1">
                        <button
                            type="button"
                            className="inline-flex h-8 items-center gap-0.5 rounded-md px-0.5 text-neutral-600 hover:bg-neutral-100"
                            aria-expanded={expanded}
                            aria-label={
                                expanded
                                    ? `Collapse grouped entries (${entries.length})`
                                    : `Expand grouped entries (${entries.length})`
                            }
                            onClick={(event) => {
                                event.stopPropagation();
                                toggleGroup(groupKey);
                            }}
                        >
                            <span
                                className="text-[10px] font-normal tabular-nums text-neutral-400"
                                aria-hidden
                            >
                                ({entries.length})
                            </span>
                            {expanded ? (
                                <ChevronDown className="size-4 shrink-0" aria-hidden />
                            ) : (
                                <ChevronRight className="size-4 shrink-0" aria-hidden />
                            )}
                        </button>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                        <span className="text-sm font-medium text-neutral-700">
                            {groupedUsageLabel}
                        </span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-left font-semibold tabular-nums text-neutral-900">
                        {groupAmountText}
                    </TableCell>
                    <TableCell className="text-left">
                        <LinkedArtifactCell
                            artifactType={artifactSample?.linkedArtifactType}
                            artifactId={artifactSample?.linkedArtifactId}
                        />
                    </TableCell>
                    <TableCell className="max-w-xs">
                        <p
                            className="max-w-sm truncate font-medium text-neutral-950"
                            title={compositeDescription}
                        >
                            {compositeDescription}
                        </p>
                    </TableCell>
                    <TableCell>
                        <div className="flex flex-wrap gap-1">
                            {poolTypesInGroup.map((poolType) => (
                                <span
                                    key={poolType}
                                    className={cn(
                                        adminCreditBadgeClassName,
                                        ledgerPoolBadgeClass(poolType),
                                    )}
                                >
                                    {getPoolLabel(poolType)}
                                </span>
                            ))}
                        </div>
                    </TableCell>
                    <TableCell>
                        <CreatedByUserCell
                            name={oldest?.createdByUserName}
                            email={oldest?.createdByUserEmail}
                        />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-neutral-700">
                        <LedgerTimestamp iso={latestTs} />
                    </TableCell>
                    <TableCell className="w-20">
                        <div className="flex items-center justify-end">
                            <LedgerRowActionsMenu entry={representativeEntry} {...rowActionProps} />
                        </div>
                    </TableCell>
                </TableRow>
                {expanded
                    ? ledgerEntriesNewestFirst(entries).map((entry, index, arr) => (
                          <LedgerEntryRow
                              key={entry.id}
                              entry={entry}
                              onShowDetails={setDetailsEntry}
                              rowActionProps={rowActionProps}
                              nested
                              isFirstInNestedBlock={index === 0}
                              isLastInNestedBlock={index === arr.length - 1}
                          />
                      ))
                    : null}
            </Fragment>
        );
    };

    const totalCount = viewMode === 'raw' ? rawEntries.length : groupedEntries.length;
    const sectionDefs: Array<{ key: 'pending' | 'committed'; label: string }> = [
        { key: 'pending', label: 'Reserved' },
        { key: 'committed', label: 'Spent' },
    ];

    return (
        <div className="space-y-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="admin-tab-switcher-track w-fit">
                    <LedgerViewTabButton
                        active={viewMode === 'grouped'}
                        onClick={() => setViewMode('grouped')}
                        icon={Layers}
                        label="Grouped"
                    />
                    <LedgerViewTabButton
                        active={viewMode === 'raw'}
                        onClick={() => setViewMode('raw')}
                        icon={List}
                        label="Raw"
                    />
                </div>
                {availablePoolTypes.length > 1 ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-neutral-600">Pool Filter</span>
                        {availablePoolTypes.map((poolType) => {
                            const isIncluded = !excludedPoolTypes.has(poolType);
                            return (
                                <button
                                    key={poolType}
                                    type="button"
                                    onClick={() => togglePoolType(poolType)}
                                    aria-pressed={isIncluded}
                                    className={cn(
                                        adminCreditBadgeClassName,
                                        'px-2.5 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#276db0]/35',
                                        ledgerPoolBadgeClass(poolType),
                                        !isIncluded && 'opacity-40',
                                    )}
                                >
                                    {getPoolLabel(poolType)}
                                </button>
                            );
                        })}
                    </div>
                ) : null}
            </div>
            <Card className="overflow-x-auto">
                <TooltipProvider delayDuration={200}>
                    <Table className="min-w-[62rem] w-full [&_td:first-child]:!pl-3 [&_td:nth-child(2)]:!pl-2 [&_th:first-child]:!pl-3 [&_th:nth-child(2)]:!pl-2 sm:[&_td:first-child]:!pl-3 sm:[&_th:first-child]:!pl-3">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-10 !pr-1" />
                                <TableHead className="w-[1%] whitespace-nowrap">Type</TableHead>
                                <TableHead className="w-[1%] whitespace-nowrap">Amount</TableHead>
                                <TableHead className="w-[1%] whitespace-nowrap">Linked</TableHead>
                                <TableHead className="max-w-xs">Description</TableHead>
                                <TableHead className="w-[1%] whitespace-nowrap">Pool</TableHead>
                                <TableHead className="max-w-[10rem]">User</TableHead>
                                <TableHead className="w-[1%] whitespace-nowrap">
                                    Timestamp
                                </TableHead>
                                <TableHead className="w-20">
                                    <span className="sr-only">Actions</span>
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {totalCount === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={tableColSpan}
                                        className="py-8 text-center text-neutral-500"
                                    >
                                        {ledgerEntries.length === 0
                                            ? 'No ledger items found.'
                                            : 'No ledger items match the selected pool filters.'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sectionDefs.map((section) => {
                                    if (viewMode === 'raw') {
                                        const rows = rawEntries.filter(
                                            (entry) => ledgerEntrySectionKey(entry) === section.key,
                                        );
                                        if (rows.length === 0) return null;
                                        return (
                                            <Fragment key={section.key}>
                                                <LedgerSectionHeaderRow
                                                    label={section.label}
                                                    count={rows.length}
                                                    colSpan={tableColSpan}
                                                />
                                                {rows.map(renderRawRow)}
                                            </Fragment>
                                        );
                                    }
                                    const groups = groupedEntries.filter(
                                        (group) =>
                                            (compositeLedgerState(group.entries) === 'pending') ===
                                            (section.key === 'pending'),
                                    );
                                    if (groups.length === 0) return null;
                                    return (
                                        <Fragment key={section.key}>
                                            <LedgerSectionHeaderRow
                                                label={section.label}
                                                count={groups.length}
                                                colSpan={tableColSpan}
                                            />
                                            {groups.map(renderGroup)}
                                        </Fragment>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TooltipProvider>
            </Card>
            <LedgerEntryDetailsDialog entry={detailsEntry} onClose={() => setDetailsEntry(null)} />
        </div>
    );
}

function LedgerSectionHeaderRow({
    label,
    count,
    colSpan,
}: {
    label: string;
    count: number;
    colSpan: number;
}) {
    return (
        <TableRow>
            <TableCell
                colSpan={colSpan}
                className="py-2 text-xs font-semibold uppercase tracking-wide text-neutral-600 !bg-neutral-100"
            >
                {label}
                <span className="ml-2 font-normal text-neutral-400">{count}</span>
            </TableCell>
        </TableRow>
    );
}

function LedgerCellPlaceholder({ align = 'center' }: { align?: 'center' | 'left' }) {
    return (
        <span
            className={cn(
                'block w-full text-sm text-neutral-400',
                align === 'left' ? 'text-left' : 'text-center',
            )}
        >
            —
        </span>
    );
}

/** Two-line timestamp; full value in tooltip on hover. */
function LedgerTimestamp({ iso }: { iso: string | number }) {
    const date = new Date(iso);
    const fullTimestamp = date.toLocaleString(undefined, {
        dateStyle: 'full',
        timeStyle: 'medium',
    });

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <div className="w-fit cursor-default leading-tight">
                    <div className="whitespace-nowrap text-sm text-neutral-700">
                        {date.toLocaleDateString()}
                    </div>
                    <div className="whitespace-nowrap text-xs text-neutral-500">
                        {date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    </div>
                </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
                {fullTimestamp}
            </TooltipContent>
        </Tooltip>
    );
}

/** Created-by user: display name with email underneath. */
function CreatedByUserCell({ name, email }: { name?: string | null; email?: string | null }) {
    const trimmedName = name?.trim();
    const trimmedEmail = email?.trim();
    if (!trimmedName && !trimmedEmail) {
        return <LedgerCellPlaceholder align="left" />;
    }
    return (
        <div className="min-w-0 leading-tight">
            {trimmedName ? (
                <p className="truncate font-medium text-neutral-950" title={trimmedName}>
                    {trimmedName}
                </p>
            ) : null}
            {trimmedEmail ? (
                <p className="mt-0.5 truncate text-sm text-neutral-500" title={trimmedEmail}>
                    {trimmedEmail}
                </p>
            ) : null}
        </div>
    );
}

/** Linked artifact: prominent copyable ID with the artifact type as secondary text. */
function LinkedArtifactCell({
    artifactType,
    artifactId,
}: {
    artifactType?: string | null;
    artifactId?: string | null;
}) {
    const id = artifactId?.trim();
    const typeLabel = formatLinkedArtifactsTypeLabel(artifactType);
    if (!id) {
        return <LedgerCellPlaceholder align="left" />;
    }
    return (
        <div className="leading-tight">
            <div className="flex items-center gap-1.5">
                <span
                    className="max-w-[9rem] truncate font-mono text-xs font-semibold text-neutral-900"
                    title={id}
                >
                    {id}
                </span>
                <CreditCopyButton value={id} label="Copy linked artifact ID" />
            </div>
            {typeLabel ? (
                <p className="text-[11px] capitalize text-neutral-500">{typeLabel}</p>
            ) : null}
        </div>
    );
}

type LedgerRowActionProps = {
    ledgerEntries: LedgerEntrySummary[];
    onAdjustRow: (entry: LedgerEntrySummary) => void;
    onRefundRow: (entry: LedgerEntrySummary) => void;
    onReassignRow: (entry: LedgerEntrySummary) => void;
    onCommitReservation: (entry: LedgerEntrySummary) => void;
    onCancelReservation: (entry: LedgerEntrySummary) => void;
    committingReservationId: string | null;
    cancellingReservationId: string | null;
};

function LedgerEntryRow({
    entry,
    onShowDetails,
    rowActionProps,
    nested = false,
    isFirstInNestedBlock = false,
    isLastInNestedBlock = false,
}: {
    entry: LedgerEntrySummary;
    onShowDetails: (entry: LedgerEntrySummary) => void;
    rowActionProps: LedgerRowActionProps;
    nested?: boolean;
    isFirstInNestedBlock?: boolean;
    isLastInNestedBlock?: boolean;
}) {
    const amountText = formatLedgerUsageAmount(getLedgerUsageAmount(entry));

    return (
        <TableRow
            className={cn(
                nested && 'bg-[#276db0]/[0.07] hover:bg-[#276db0]/[0.1]',
                nested && isFirstInNestedBlock && 'border-t-2 border-t-neutral-300',
                nested && isLastInNestedBlock && 'border-b-2 border-b-neutral-300',
            )}
        >
            <TableCell className="w-10 !pr-1" />
            <TableCell
                className={cn('whitespace-nowrap', nested && 'border-l-4 border-l-[#276db0] pl-4')}
            >
                <span className="text-sm font-medium text-neutral-700">
                    {getLedgerTypeDisplayLabel(entry)}
                </span>
            </TableCell>
            <TableCell className="whitespace-nowrap text-left font-medium tabular-nums text-neutral-900">
                {amountText}
            </TableCell>
            <TableCell className="text-left">
                <LinkedArtifactCell
                    artifactType={entry.linkedArtifactType}
                    artifactId={entry.linkedArtifactId}
                />
            </TableCell>
            <TableCell className="max-w-xs">
                {entry.description?.trim() ? (
                    <p
                        className="max-w-sm truncate font-medium text-neutral-950"
                        title={entry.description}
                    >
                        {entry.description}
                    </p>
                ) : (
                    <LedgerCellPlaceholder />
                )}
            </TableCell>
            <TableCell className="whitespace-nowrap">
                <span
                    className={cn(adminCreditBadgeClassName, ledgerPoolBadgeClass(entry.poolType))}
                >
                    {getPoolLabel(entry.poolType)}
                </span>
            </TableCell>
            <TableCell>
                <CreatedByUserCell
                    name={entry.createdByUserName}
                    email={entry.createdByUserEmail}
                />
            </TableCell>
            <TableCell className="whitespace-nowrap">
                <LedgerTimestamp iso={entry.createdAt} />
            </TableCell>
            <TableCell className="w-20">
                <div className="flex items-center justify-end gap-2">
                    <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="size-8 shrink-0"
                        aria-label="Details"
                        onClick={() => onShowDetails(entry)}
                    >
                        <Info className="size-4" aria-hidden />
                    </Button>
                    <div className="size-8 shrink-0">
                        <LedgerRowActionsMenu entry={entry} {...rowActionProps} />
                    </div>
                </div>
            </TableCell>
        </TableRow>
    );
}

const LEDGER_ROW_MENU_MIN_WIDTH_PX = 136;
const LEDGER_ROW_MENU_GAP_PX = 4;
const LEDGER_ROW_MENU_VIEWPORT_PADDING_PX = 8;

function computeLedgerRowMenuPosition(
    trigger: HTMLElement,
    menu: HTMLElement | null,
): { top: number; left: number } {
    const rect = trigger.getBoundingClientRect();
    const menuHeight = menu?.offsetHeight ?? 0;
    const menuWidth = Math.max(menu?.offsetWidth ?? 0, LEDGER_ROW_MENU_MIN_WIDTH_PX);
    const estimatedHeight = menuHeight > 0 ? menuHeight : 140;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward =
        spaceBelow < estimatedHeight + LEDGER_ROW_MENU_GAP_PX && spaceAbove > spaceBelow;
    const top = openUpward
        ? Math.max(
              LEDGER_ROW_MENU_VIEWPORT_PADDING_PX,
              rect.top - estimatedHeight - LEDGER_ROW_MENU_GAP_PX,
          )
        : rect.bottom + LEDGER_ROW_MENU_GAP_PX;
    const left = Math.max(
        LEDGER_ROW_MENU_VIEWPORT_PADDING_PX,
        Math.min(
            rect.right - menuWidth,
            window.innerWidth - menuWidth - LEDGER_ROW_MENU_VIEWPORT_PADDING_PX,
        ),
    );

    return { top, left };
}

function LedgerRowActionsMenu({
    entry,
    ledgerEntries,
    onAdjustRow,
    onRefundRow,
    onReassignRow,
    onCommitReservation,
    onCancelReservation,
    committingReservationId,
    cancellingReservationId,
}: LedgerRowActionProps & {
    entry: LedgerEntrySummary;
}) {
    const [open, setOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const showAdjust = canAdjustAdminLedgerRow(entry, ledgerEntries);
    const showRefund = canRefundAdminLedgerRow(entry, ledgerEntries);
    const showReassign = canReassignAdminLedgerRow(entry, ledgerEntries);
    const showCancel = canCancelPendingCreditReservation(entry);
    const showCommit = showCancel;
    const reservationId = entry.creditReservationId?.trim() ?? null;
    const isCancelling = reservationId !== null && cancellingReservationId === reservationId;
    const isCommitting = reservationId !== null && committingReservationId === reservationId;

    useLayoutEffect(() => {
        if (!open) {
            setMenuPosition(null);
            return;
        }

        const updateMenuPosition = () => {
            const trigger = triggerRef.current;
            if (!trigger) {
                return;
            }
            setMenuPosition(computeLedgerRowMenuPosition(trigger, menuRef.current));
        };

        updateMenuPosition();
        const frame = requestAnimationFrame(updateMenuPosition);
        window.addEventListener('scroll', updateMenuPosition, true);
        window.addEventListener('resize', updateMenuPosition);

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', updateMenuPosition, true);
            window.removeEventListener('resize', updateMenuPosition);
        };
    }, [open, showAdjust, showCancel, showCommit, showRefund, showReassign]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) {
                return;
            }
            setOpen(false);
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [open]);

    if (!showAdjust && !showRefund && !showReassign && !showCommit && !showCancel) {
        return null;
    }

    const runAction = (action: () => void) => {
        setOpen(false);
        action();
    };

    const menu = open ? (
        <div
            ref={menuRef}
            role="menu"
            style={{
                position: 'fixed',
                top: menuPosition?.top ?? 0,
                left: menuPosition?.left ?? 0,
                visibility: menuPosition ? 'visible' : 'hidden',
                zIndex: 50,
            }}
            className="min-w-[8.5rem] rounded-md border border-neutral-200 bg-white py-1 shadow-lg ring-1 ring-black/5"
            onClick={(event) => event.stopPropagation()}
        >
            {showAdjust ? (
                <button
                    type="button"
                    role="menuitem"
                    className="flex w-full px-3 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                    onClick={() => runAction(() => onAdjustRow(entry))}
                >
                    Adjust
                </button>
            ) : null}
            {showReassign ? (
                <button
                    type="button"
                    role="menuitem"
                    className="flex w-full px-3 py-2 text-left text-sm text-neutral-700 hover:bg-neutral-50"
                    onClick={() => runAction(() => onReassignRow(entry))}
                >
                    Reassign
                </button>
            ) : null}
            {showRefund ? (
                <button
                    type="button"
                    role="menuitem"
                    className="flex w-full px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50"
                    onClick={() => runAction(() => onRefundRow(entry))}
                >
                    Refund
                </button>
            ) : null}
            {showCommit ? (
                <button
                    type="button"
                    role="menuitem"
                    className="flex w-full px-3 py-2 text-left text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                    disabled={isCommitting || isCancelling}
                    onClick={() => runAction(() => onCommitReservation(entry))}
                >
                    {isCommitting ? 'Committing…' : 'Commit'}
                </button>
            ) : null}
            {showCancel ? (
                <button
                    type="button"
                    role="menuitem"
                    className="flex w-full px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                    disabled={isCancelling || isCommitting}
                    onClick={() => runAction(() => onCancelReservation(entry))}
                >
                    {isCancelling ? 'Cancelling…' : 'Cancel'}
                </button>
            ) : null}
        </div>
    ) : null;

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className="inline-flex size-8 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#276db0]/35"
                aria-label="Row actions"
                aria-expanded={open}
                aria-haspopup="menu"
                onClick={(event) => {
                    event.stopPropagation();
                    setOpen((current) => !current);
                }}
            >
                <MoreVertical className="size-4" aria-hidden />
            </button>
            {menu ? createPortal(menu, document.body) : null}
        </>
    );
}

function LedgerViewTabButton({
    active,
    icon: Icon,
    label,
    onClick,
}: {
    active: boolean;
    icon: LucideIcon;
    label: string;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold text-neutral-950 transition-colors',
                active ? 'border border-neutral-400 bg-white shadow-sm' : 'hover:bg-neutral-300/45',
            )}
        >
            <Icon className="size-4" aria-hidden />
            {label}
        </button>
    );
}
